import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import i18n from '@/i18n';
import { formatSqliteUtcDateTime } from '@/lib/date';
import type { AuditEntry } from '@/types/admin';

function formatValue(field: string, value: unknown): string {
  if (value === null || value === undefined || value === '') {
    return i18n.t('admin:audit.emptyValue');
  }
  if (field === 'is_enabled' || field === 'admin_locked') {
    if (value === 1 || value === true) return i18n.t('admin:audit.yes');
    if (value === 0 || value === false) return i18n.t('admin:audit.no');
  }
  if (field === 'event_type' && (value === 'annual' || value === 'specific')) {
    return i18n.t(`admin:events.${value}`);
  }
  return String(value);
}

/* Audit entries as hairline rows: action label, event link, actor and monospace timestamp. */
export function AuditList({
  entries,
  showEvent = false,
}: {
  entries: AuditEntry[];
  showEvent?: boolean;
}) {
  const { t } = useTranslation('admin');
  if (entries.length === 0) {
    return <p className="m-0 py-4 text-sm text-ink-2">{t('audit.empty')}</p>;
  }
  return (
    <ul className="m-0 list-none divide-y divide-rule p-0">
      {entries.map((entry) => (
        <li key={entry.id} className="space-y-1 py-3 text-sm">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <span className="status-label text-ink">
              {t(`audit.actions.${entry.action}`)}
            </span>
            {showEvent && (
              <Link to={`/admin/events/${entry.event_id}`} className="text-ink hover:text-accent-text">
                {entry.event_title ?? entry.event_uid}
              </Link>
            )}
            <span className="text-xs text-ink-2">
              {entry.actor_name ?? entry.actor_sub} · <span className="font-mono">{formatSqliteUtcDateTime(entry.created_at)}</span>
            </span>
          </div>
          {entry.action !== 'create' && Object.keys(entry.changes).length > 0 && (
            <ul className="m-0 list-none space-y-0.5 p-0 text-xs text-ink-2">
              {Object.entries(entry.changes).map(([field, [before, after]]) => (
                <li key={field} className="break-words">
                  {t('audit.fieldChange', {
                    field: t(`fields.${field}`, { defaultValue: field }),
                    change: t('audit.valueChange', {
                      before: formatValue(field, before), after: formatValue(field, after),
                    }),
                  })}
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );
}
